import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";

const pillars = [
    {
        id: "design",
        color: "var(--st-green)",
        title: "Design",
        tagline: "Identities. Interiors of the web. Worlds you can touch.",
        services: ["Brand Identity", "Digital Marketing", "UI / UX", "Events & Spaces"],
        to: "/design",
    },
    {
        id: "consult",
        color: "var(--st-coral)",
        title: "Consult",
        tagline: "Strategy sessions for founders standing at a fork in the road.",
        services: ["Brand Audits", "Market Positioning", "Go-to-Market", "Startup Mentorship"],
        to: "/consult",
    },
    {
        id: "learn",
        color: "var(--st-purple)",
        title: "Learn",
        tagline: "15 years of studio practice, distilled into workshops and cohorts.",
        services: ["Workshops", "1:1 Mentorship", "Online Courses", "Community"],
        to: "/learn",
    },
];

const Pillars = () => {
    return (
        <section
            data-testid="pillars-section"
            id="pillars"
            className="relative z-10 bg-[var(--st-cream)] py-32 lg:py-48"
        >
            <div className="st-container">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-24 lg:mb-32">
                    <div className="lg:col-span-6">
                        <p className="eyebrow mb-8 text-[var(--st-gold-deep)]">01 — Three rooms, one studio</p>
                        <h2 className="font-serif text-5xl md:text-6xl lg:text-7xl leading-[0.98]">
                            What we do,<br /> and <span className="italic">how it travels.</span>
                        </h2>
                    </div>
                    <div className="lg:col-span-5 lg:col-start-8 flex items-end">
                        <p className="text-base lg:text-lg leading-relaxed text-[var(--st-ink-soft)] max-w-md">
                            Most studios pick a lane. We've spent fifteen years discovering
                            that the founders we love working with usually need more than one.
                            So we built three. Three rooms, one studio — and you're welcome
                            in all of them.
                        </p>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[var(--st-line)] border border-[var(--st-line)]">
                    {pillars.map((p, i) => (
                        <motion.div
                            key={p.id}
                            data-testid={`pillar-card-${p.id}`}
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-80px" }}
                            transition={{ duration: 0.7, delay: i * 0.12 }}
                            className="bg-[var(--st-cream)] p-8 lg:p-12 flex flex-col group hover:bg-[var(--st-cream-soft)] transition-colors duration-500"
                        >
                            <div
                                className="w-8 h-8 mb-8"
                                style={{ background: p.color }}
                            />
                            <div className="mb-3">
                                <span className="eyebrow opacity-60">0{i + 1}</span>
                            </div>
                            <h3 className="font-serif text-5xl lg:text-6xl mb-4" style={{ color: p.color }}>
                                {p.title}
                            </h3>
                            <p className="font-serif italic text-xl text-[var(--st-ink)] mb-8 max-w-xs">
                                {p.tagline}
                            </p>
                            <ul className="space-y-2 mb-10 flex-1">
                                {p.services.map((s) => (
                                    <li key={s} className="text-sm text-[var(--st-ink-soft)] flex items-center gap-2">
                                        <span
                                            className="inline-block w-1.5 h-1.5 rounded-full"
                                            style={{ background: p.color }}
                                        />
                                        {s}
                                    </li>
                                ))}
                            </ul>
                            <Link
                                to={p.to}
                                data-testid={`pillar-link-${p.id}`}
                                className="inline-flex items-center gap-2 text-sm uppercase tracking-widest mt-auto group-hover:translate-x-1 transition-transform"
                            >
                                Visit the {p.title.toLowerCase()} room
                                <ArrowUpRight size={16} />
                            </Link>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default Pillars;
